// physical-tests-data.js - pure helpers for the Physical Testing test library
// (V6_DESIGN section 7.11.1). The built-in tests (Bronco + the original 8) stay
// in CONFIG.baseline; custom tests live in the physical_tests table. These
// helpers normalise a custom DB row into the same shape as a CONFIG exercise and
// merge the two into one pickable library. No DOM, no network - unit-tested.

// Map a library `type` to the tester UI `template` the existing engine knows.
// Custom tests reuse the stopwatch / rep-counter templates; the bespoke gate
// scorer stays a built-in-only template for now (refined in P2).
export function typeToTemplate(type) {
  switch (type) {
    case 'reps':     return 'reps';
    case 'distance': return 'reps';     // numeric entry, counts up
    case 'gate':     return 'gates';
    case 'countdown':
    case 'stopwatch':
    default:         return 'stopwatch';
  }
}

const CUSTOM_DOMAIN = 'Custom tests';

/** Normalise a physical_tests row into the CONFIG-exercise shape the app uses. */
export function normalizeCustomTest(row) {
  if (!row || !row.key) return null;
  return {
    key: row.key,
    name: row.name || row.key,
    domain: CUSTOM_DOMAIN,
    template: typeToTemplate(row.type),
    better: row.better === 'max' ? 'max' : 'min',
    unit: row.unit || null,
    tags: Array.isArray(row.tags) ? row.tags : [],
    custom: true,
  };
}

/**
 * Merge built-in CONFIG exercises with active custom rows into one library.
 * A built-in key always wins over a custom one with the same key (so a custom
 * test can never shadow a bespoke built-in). Returns CONFIG shape objects.
 */
export function mergeTestLibrary(builtins, customRows) {
  const out = (builtins || []).slice();
  const seen = new Set(out.map(e => e.key));
  (customRows || []).forEach(r => {
    if (!r || r.active === false) return;
    const n = normalizeCustomTest(r);
    if (!n || seen.has(n.key)) return;
    seen.add(n.key);
    out.push(n);
  });
  return out;
}

/** Validate a director-entered custom test before saving. */
export function validateCustomTest({ name, type } = {}) {
  const n = (name || '').trim();
  if (!n) return { ok: false, reason: 'need_name' };
  const types = ['stopwatch', 'countdown', 'reps', 'distance', 'gate'];
  if (type && !types.includes(type)) return { ok: false, reason: 'bad_type' };
  // Stable key from the name: lowercase, non-alphanumerics to underscores.
  const key = 'custom_' + n.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);
  if (key === 'custom_') return { ok: false, reason: 'need_name' };
  return { ok: true, key };
}
